import { useMutation } from '@tanstack/react-query';
import UserService from '~/modules/services/user-service';
import { useContext } from 'react';
import { SessionContext } from '~/modules/contexts';
import AuthService from '~/modules/services/auth-service';
import { setupAPI } from '~/modules/services/api-service';
import RouteService from '~/modules/services/route-service';
import StorageService from '~/modules/services/storage-service';
import { handleError } from '~/utils/handle-error';
import { completeDocsHandshake, DOCS_AUTH_PATH } from '~/utils/docs-handshake';
import SessionService from '~/modules/services/session-service';
import AsperaService from '~/modules/services/aspera-service';

export default function useAuth() {
	let {
		auth, setAuth
	} = useContext(SessionContext);

	const redirectAfterLogin = (session) => {
		if( session?.guestAccessShare ) {
			StorageService.remove('lastRoute');
			RouteService.navigateTo('share-view', { id: session.guestAccessShare });
			return;
		}

		// A docs SSO visitor signs in on `/docs/auth?redirect=…` (the login modal overlays the
		// route whose auth gate just failed, so its minting page never mounted). Now that we're
		// authenticated, complete the handshake straight to the Papervine callback instead of
		// falling through to the SPA. The destination rides in the URL, which the handshake reads
		// itself — `lastRoute` is cookie-backed and easily stale, so we don't rely on it.
		if( window.location.pathname === DOCS_AUTH_PATH ) {
			StorageService.remove('lastRoute');
			completeDocsHandshake();
			return;
		}

		const redirectUrl = StorageService.get('lastRoute');
		if( redirectUrl && !redirectUrl.includes('/login') && !redirectUrl.includes('/register') && !redirectUrl.includes('/guest') ) {
			StorageService.remove('lastRoute');
			window.location.href = redirectUrl;
		}
		else {
			RouteService.navigateTo('projects');
		}
	};

	const handleAuthenticatedSessionSuccess = (session, options = {}) => {
		const { redirect = true } = options;
		setupAPI(session, true);
		setAuth({ session, isLoggedIn: true, lastAttemptedAt: new Date() });
		if( redirect ) {
			redirectAfterLogin(session);
		}
	};

	/*
		Login mutation
	*/
	const {
		mutateAsync: login,
		isLoading: isLoggingIn
	} = useMutation({
		mutationKey: ['session'],
		mutationFn: AuthService.login,
		onMutate: () => {
			setAuth({ ...auth, lastAttemptedAt: new Date(), loginError: null });
		},
		onSuccess: (session) => {
			if( session.twofactor ) {
				setAuth({ ...auth, isTwoFactorLogin: true });

				// Redirect to login page if current location is register page
				if( window.location.pathname.includes('/register') ) {
					window.location.replace('/login');
				}

				return;
			}
			handleAuthenticatedSessionSuccess(session);
		},
		onError: (e) => {
			const err = handleError(e.status === 403
				? {
					...e,
					message: 'Invalid username or password'
				}
				: e);
			setAuth({ ...auth, loginError: err });
		}
	});

	const {
		mutateAsync: guestLogin,
		isLoading: isGuestLoggingIn
	} = useMutation({
		mutationKey: ['guestSession'],
		mutationFn: AuthService.guestLogin,
		onMutate: () => {
			setAuth({ ...auth, lastAttemptedAt: new Date(), loginError: null });
		},
		onSuccess: (session, variables) => {
			handleAuthenticatedSessionSuccess(session, {
				redirect: variables?.redirect !== false
			});
		},
		onError: (e) => {
			setAuth({ ...auth, loginError: handleError(e) });
		}
	});

	/* Reset Password mutation */
	const {
		mutate: resetPassword,
		isLoading: isResettingPassword
	} = useMutation({
		mutationKey: ['resetPassword'],
		mutationFn: AuthService.resetPassword,
		onSuccess: () => {
			setAuth({ ...auth, resetPasswordSuccess: true, resetPasswordError: null });
		},
		onError: (e) => {
			setAuth({ ...auth, resetPasswordSuccess: false, resetPasswordError: handleError(e) });
		}
	});

	/*
		request Reset password mutation
	*/
	const {
		mutate: requestResetPassword
	} = useMutation({
		mutationKey: ['resetPassword'],
		mutationFn: AuthService.requestResetPassword,
		onSuccess: () => {
			setAuth({ ...auth, resetPasswordSuccess: true, resetPasswordError: null });
		},
		onError: (e) => {
			setAuth({ ...auth, resetPasswordSuccess: false, resetPasswordError: handleError(e) });
		}
	});

	/*
		Reset password feedback
	 */
	const clearResetPasswordFeedback = () => {
		setAuth({ ...auth, resetPasswordSuccess: false, resetPasswordError: null });
	};

	/*
		Two factor login
	 */
	const {
		mutate: twoFactorLogin,
		isLoading: isTwoFactorLoggingIn
	} = useMutation({
		mutationKey: ['twoFactorLogin'],
		mutationFn: (token) => AuthService.twoFactorLogin(token),
		onSuccess: ({ data }) => {
			setAuth({ ...auth, twoFactorLoginSuccess: true });
			// Fire-and-forget: login's own onError surfaces a failure, so swallow the
			// mutateAsync rejection to avoid a duplicate unhandled-rejection report.
			login({ username: data?.emailNormal || data?.email || '', token: data?.token ?? '', isTwoFactor: true })
				.catch(() => { /* handled by onError */ });
		},
		onError: (e) => {
			const error = handleError(e);
			setAuth({ ...auth, twoFactorLoginSuccess: false, twoFactorLoginError: error?.message || 'An error occurred' });
		}
	});

	/*
		Resume session
	 */

	const { mutateAsync: resumeSession } = useMutation(
		['session'],
		() => AuthService.resumeSession(),
		{
			onSuccess: (session) => {
				setupAPI(session);
				const token = AuthService.getToken();
				AsperaService.setCredentials(session, token);
				setAuth({
					...auth,
					session,
					isLoggedIn: true,
					lastAttemptedAt: new Date(),
					lastUserEmail: session.emailNormal
				});
			},
			onError: () => {
				setAuth({ ...auth, session: null, loginError: null, lastAttemptedAt: new Date() });
			}
		}
	);

	/*
		Get session
	*/

	const { mutateAsync: getSession } = useMutation(
		['getSession'],
		() => SessionService.getSession(),
		{
			onSuccess: (session) => {
				setupAPI(session);
				setAuth({ ...auth, session, isLoggedIn: true, isLoading: true, lastLoadedAt: new Date() });
			},
			onError: () => {
				setAuth({ ...auth, session: null, isLoading: false });
			}
		}
	);

	/*
	logout mutation
	*/
	const { mutate: logout } = useMutation({
		mutationKey: ['logout'],
		mutationFn: AuthService.logout
	});

	/*
		Update user mutation
	*/

	const { mutateAsync: updateUser, isLoading: isUserSaving } = useMutation(
		(data) => UserService.updateUser(auth.session.id, data)
	);

	function isLoaded() {
		return !!auth.lastLoadedAt;
	}
	return {
		...auth,
		resumeSession,
		getSession,
		login,
		logout,
		twoFactorLogin,
		isTwoFactorLoggingIn,
		isUserSaving,
		updateUser,
		isLoggingIn,
		guestLogin,
		isGuestLoggingIn,
		guestLoginError: auth.loginError,
		isLoaded,
		isResettingPassword,
		requestResetPassword,
		resetPassword,
		clearResetPasswordFeedback

	};
}
